/**
 * Helper to generate a redux-persist filter to enable reducers to only
 * persist specific keys. It also allows us to specify default values to
 * be applied when restoring values from persisted storage back to our
 * reducers during initialization.
 *
 * @module tools/ReduxPersistFilter
 */
import { Map, OrderedMap } from 'immutable';
import isObject from 'lodash/isObject';
import { createTransform } from 'redux-persist';

function getFiltered(state, keys, inbound) {
  const toFilterState = inbound ? inbound(state) : state;
  if (Array.isArray(keys) && keys.length) {
    if (Map.isMap(toFilterState) || OrderedMap.isOrderedMap(toFilterState)) {
      return toFilterState.filter((_, key) => keys.indexOf(key) >= 0);
    } else if (isObject(toFilterState)) {
      const persist = {};
      keys.forEach(key => (persist[key] = toFilterState[key]));
      return persist;
    }
    // otherwise we pass it through because we don't know what the hell it is
  }
  return toFilterState;
}

function getUnfiltered(state, defaults, outbound, mergeDeep) {
  const outboundState = outbound ? outbound(state) : state;
  if (Map.isMap(outboundState) || OrderedMap.isOrderedMap(outboundState)) {
    if (defaults) {
      // we have to assume defaults are of the correct type...
      return mergeDeep ? defaults.mergeDeep(outboundState) : defaults.merge(outboundState);
    }
    return outboundState;
  } else if (isObject(outboundState)) {
    return defaults ? { ...defaults, ...outboundState } : outboundState;
  }
  // otherwise we pass it through because we don't know what the hell it is
  return outboundState;
}

/**
 * Use this method to create a filter to be added as a transform to our
 * redux-persist configuration. This allows for a subset of reducer keys
 * to be persisted while also ensuring default values are present when the
 * store is hydrated from the persisted state. This method can only be used
 * on reducers that return a native JavaScript object or an ImmutableJS Map or
 * OrderedMap. If an ImmutableJS type is used, that is the type that will be
 * passed to an _inbound_ and _outbound_ method and should be the type returned
 * by those methods as well (if provided).
 *
 * Also note when using defaults with Maps or OrderedMaps it will default to
 * shallow merges. For deep merges use the mergeDeep boolean parameter. For any
 * other custom behavior use the outbound method to override the result.
 *
 * @param {string} reducer The name of the reducer to filter
 * @param {object | Map} defaults The defaults to be applied when restoring values
 * from persisted state
 * @param {Array} keys The subset of keys to persist from the reducer
 * @param {Function} inbound An optional function that receives and returns a state object
 * with the opportunity to modify the state before it is filtered for insertion
 * into the persistent storage
 * @param {Function} outbound An optional function that receives and returns a state object
 * with the opportunity to modify the state before it is ingested by the app from
 * the persistent storage
 * @param {boolean} mergeDeep When using Map/OrderedMap with defaults defined and
 * need a deep merge, set this to true
 * @returns {Function} A function (transform) used by redux-persist
 */
export default function createFilter({
  reducer = '',
  defaults,
  keys = [],
  inbound,
  outbound,
  mergeDeep = false,
}) {
  return createTransform(
    // returns the filtered content to be persisted, considered "inbound"
    state => getFiltered(state, keys, inbound),
    // returns the state from the store, we will apply defaults if there
    // are any provided here, considered "outbound"
    state => getUnfiltered(state, defaults, outbound, mergeDeep),
    // config property, in our case, only ever used to specify the reducer
    // we're filtering
    { whitelist: [reducer] },
  );
}
